// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import LazyImage, { LazyImageProps } from "@eisinc/MobiusOverrides/LazyImage";
import LinkRight from "@eisinc/XDL/Icons/LinkRight";
import { fontConfig } from "@eisinc/XDL/PreStyle/typography";
import { color, createLinkIconStyles, getSystemColor, xScreen } from "@eisinc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconWrapper } from "@insite/mobius/Icon";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { css } from "styled-components";

const enum fields {
    imageUrl = "imageUrl",
    link = "link",
    linkText = "linkText",
    copy = "copy",
    openInNewTab = "openInNewTab",
    showBorder = "showBorder",
    linkAlignment = "linkAlignment",
    bgColor = "bgColor",
}

interface InfoCardProps extends WidgetProps {
    fields: {
        [fields.imageUrl]: string;
        [fields.copy]: string;
        [fields.linkText]: string;
        [fields.link]: LinkFieldValue;
        [fields.openInNewTab]: boolean;
        [fields.showBorder]: boolean;
        [fields.linkAlignment]: "left" | "center" | "right";
        [fields.bgColor]: string;
    };
}

interface InfoCardTemplateWidgetStyles {
    wrapper?: ClickablePresentationProps;
    image?: LazyImageProps;
    contentWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    copy?: TypographyPresentationProps;
    link?: LinkPresentationProps;
    linkWrapper?: InjectableCss;
}

const infoCardTemplateWidgetStyles: InfoCardTemplateWidgetStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: center;
            gap: 12px;
            box-shadow: inset 0 4px 0 0 ${color("primary")};
            padding-top: 4px;
            height: 100%;
            ${({ showBorder }: any) =>
                showBorder &&
                css`
                    border: 1px solid ${color("common.border")};
                `}
            ${({ bgColor }: any) =>
                bgColor &&
                css`
                    background-color: ${color(bgColor)};
                `}
        `,
    },
    image: {
        css: css`
            max-height: 178px;
            ${xScreen("lte", "md")} {
                max-height: 261px;
            }
            ${xScreen("lt", "sm")} {
                max-height: 184px;
            }
        `,
    },
    contentWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: center;
            gap: 8px;
            padding: 0 12px 12px;
            flex: 1;
            width: 100%;
        `,
    },
    title: {
        variant: "h4",
        forwardAs: "p",
        css: css`
            margin-bottom: 0;
        `,
    },
    copy: {
        ...fontConfig.small,
        css: css`
            margin-bottom: 0;
            flex: 1;
            ${TypographyStyle} {
                font-size: ${fontConfig.small.size}px;
                line-height: ${fontConfig.small.lineHeight};
            }
        `,
    },
    link: {
        ...createLinkIconStyles({
            icon: LinkRight,
            sizeVariant: "large",
            linkStyles: {
                icon: { position: "right" },
                css: css`
                    ${({ linkAlignment }: any) =>
                        linkAlignment === "right"
                            ? css`
                                  align-self: flex-end;
                              `
                            : linkAlignment === "center"
                              ? css`
                                    align-self: center;
                                `
                              : ""}
                `,
            },
        }),
    },
    linkWrapper: {
        css: css`
            display: inline-flex;
            align-items: center;
            &:hover,
            &:focus {
                ${TypographyStyle}, ${IconWrapper} svg {
                    color: ${color("common.ui9")};
                }
                ${IconWrapper} {
                    opacity: 0.5;
                }
            }
            ${({ linkAlignment }: any) =>
                linkAlignment === "right"
                    ? css`
                          align-self: flex-end;
                      `
                    : linkAlignment === "center"
                      ? css`
                            align-self: center;
                        `
                      : css`
                            align-self: flex-start;
                        `}
        `,
    },
};

type Props = InfoCardProps;

const styles = infoCardTemplateWidgetStyles;
const InfoCardTemplateWidget: React.FC<Props> = ({ fields }) => {
    const url = useGetLink(fields.link)?.url;

    return (
        <Clickable
            {...styles.wrapper}
            href={url}
            target={fields.openInNewTab ? "_blank" : undefined}
            aria-label={fields.linkText}
            {...{ showBorder: fields.showBorder, bgColor: fields.bgColor }}
        >
            <LazyImage altText={fields.linkText} {...styles.image} src={fields.imageUrl} />
            <StyledWrapper {...styles.contentWrapper}>
                <Typography {...styles.title}>{fields.linkText}</Typography>
                {fields.copy && <Typography {...styles.copy}>{fields.copy}</Typography>}
                <StyledWrapper {...styles.linkWrapper} {...{ linkAlignment: fields.linkAlignment }}>
                    <Typography {...styles.link?.typographyProps} forwardAs="span" color="primary">
                        {translate("Learn More")}
                    </Typography>
                    {styles.link?.icon?.iconProps && <Icon {...styles.link.icon.iconProps} color="primary" />}
                </StyledWrapper>
            </StyledWrapper>
        </Clickable>
    );
};

export const definition: WidgetDefinition = {
    group: "Basic",
    fieldDefinitions: [
        {
            name: fields.imageUrl,
            fieldType: "General",
            editorTemplate: "ImagePickerField",
            defaultValue: "",
            isRequired: true,
        },
        {
            name: fields.link,
            displayName: "Link",
            editorTemplate: "LinkField",
            defaultValue: { value: "", type: "Page" },
            fieldType: "General",
            isRequired: true,
            validate: (link: LinkFieldValue) => {
                if (!link || !link.value) {
                    return "A value must be provided.";
                }
                return null;
            },
        },
        {
            name: fields.linkText,
            displayName: "Link Label",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "Translatable",
            isRequired: true,
            validate: (value: string | null) => {
                if (value && value.length > 40) {
                    return "Max length is 40 characters";
                }
                return null;
            },
        },
        {
            name: fields.linkAlignment,
            editorTemplate: "DropDownField",
            defaultValue: "left",
            fieldType: "General",
            options: [
                {
                    displayName: "Left",
                    value: "left",
                },
                {
                    displayName: "Center",
                    value: "center",
                },
                {
                    displayName: "Right",
                    value: "right",
                },
            ],
            hideEmptyOption: true,
        },
        {
            name: fields.openInNewTab,
            displayName: "Open in New Tab",
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "General",
        },
        {
            name: fields.copy,
            displayName: "Copy",
            editorTemplate: "MultilineTextField",
            defaultValue: "",
            fieldType: "Translatable",
            isRequired: false,
            validate: (value: string | null) => {
                if (value && value.length > 200) {
                    return "Max length is 200 characters";
                }
                return null;
            },
        },
        {
            name: fields.showBorder,
            displayName: "Show Border",
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "General",
        },
        {
            name: fields.bgColor,
            displayName: "Background Color",
            editorTemplate: "ColorPickerField",
            defaultValue: getSystemColor("common.white"),
            isRequired: true,
            fieldType: "General",
            popoverLocation: "top",
        },
    ],
};

const widgetModule: WidgetModule = {
    component: InfoCardTemplateWidget,
    definition,
};

export default widgetModule;
